import React from 'react';

import { z } from 'zod';

import { cn } from 'components/lib/utils';

import { templateStudioSchema } from '../../../validation-schema';

export type FreeTilesLayoutValue = NonNullable<
  z.infer<typeof templateStudioSchema>['buyOneGetMany']
>['freeTilesLayout'];

interface IFreeTilesLayoutOptionProps {
  value: FreeTilesLayoutValue;
  isSelected: boolean;
  onClick: (value: FreeTilesLayoutValue) => void;
  testId: string;
}

const FreeTilesLayoutOption: React.FC<IFreeTilesLayoutOptionProps> = ({
  value,
  isSelected,
  onClick,
  testId
}): React.ReactElement => {
  const handleClick = (): void => {
    onClick(value);
  };

  return (
    <button
      type="button"
      role="radio"
      aria-checked={isSelected}
      aria-label={`${value} free tiles`}
      className={cn(
        'flex gap-1 w-[80px] h-[38px] p-[10px] rounded-lg border border-base bg-base-background hover:shadow-md cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-base-ring',
        isSelected && 'border-base-black'
      )}
      onClick={handleClick}
      data-testid={testId}
    >
      {Array.from({ length: value }).map((_, index) => (
        <div key={index} className="w-full h-full bg-base-muted rounded-sm" />
      ))}
    </button>
  );
};

const FREE_TILES_LAYOUT_OPTIONS: FreeTilesLayoutValue[] = [3, 2, 1];

interface IFreeTilesLayoutPickerProps {
  value: FreeTilesLayoutValue;
  onChange: (value: FreeTilesLayoutValue) => void;
}

const FREE_TILES_LAYOUT_PICKER_LABEL_ID =
  'TemplatesStudio-BuyOneGetMany-freeTilesLayout-label';

export const FreeTilesLayoutPicker: React.FC<IFreeTilesLayoutPickerProps> = ({
  value,
  onChange
}): React.ReactElement => {
  return (
    <div className="flex flex-col gap-2">
      <div
        id={FREE_TILES_LAYOUT_PICKER_LABEL_ID}
        className="text-sm text-base-black text-left mt-1 mb-2"
      >
        Free Tiles Layout
      </div>
      <div
        role="radiogroup"
        aria-labelledby={FREE_TILES_LAYOUT_PICKER_LABEL_ID}
        className="flex justify-between items-center w-full text-base-mutedForeground text-xs leading-5 font-sans"
      >
        {FREE_TILES_LAYOUT_OPTIONS.map((option) => (
          <FreeTilesLayoutOption
            key={option}
            value={option}
            isSelected={value === option}
            onClick={onChange}
            testId={`TemplatesStudio-BuyOneGetMany-freeTilesLayout-${option}`}
          />
        ))}
      </div>
    </div>
  );
};
